import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Input, Select, Space } from 'antd';
import { FlowsheetContext } from '../../FlowsheetProvider'
import { NodeState, LineRecord, SetOperation } from '../../services/modal'
import { UnitSelect, UnitStates } from '../UnitSelect/UnitSelect'
import { getUnitAttributes } from '../Header/Simulation/Workbook/VariableTable/attributes'
import { CheckOutlined, CloseOutlined, DeleteOutlined, FormOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { v4 as uuidv4 } from "uuid";
import { addSetOperation, getSetOperations, removeSetOperation, updateSetOperation } from '@/store/flowsheet/flowsheet.slice'
import { tagGenerator } from '../../services/TagGenerator'
import { EquipmentType } from '@/types/common'
import { ManualInput } from '../RightPanel/UnitPanel/components/common/ManualInput'
import { getVarAttribute } from '../RightPanel/UnitPanel/components/common/utils'
import styles from './styles.module.less'
import { useGetUnitEntity } from '@/pages/Project/hooks/useUnitEntity'
import { UnitMessageDialog } from '../RightPanel/UnitPanel/components/common/UnitMessage'

interface EditData extends SetOperation {
  index: number
}

interface OperationsTableProps {
  onChange?: () => void
  data: any
}

export const OperationsTable = (props: OperationsTableProps) => {
  const {onChange, data = []} = props
  const [editRow, setEditRow] = useState<EditData | undefined>(undefined)
  const {getAllUnits} = useContext(FlowsheetContext)
  const [units, setUnits] = useState<UnitStates>({nodes: [], lines: [], operations: []})
  const operations = useSelector(getSetOperations)
  const dispatch = useDispatch()
  const getUnitEntity = useGetUnitEntity()

  useEffect(() => {
    getAllUnits().then((units) => {
      setUnits(units)
    })
  }, [])

  const unitMap = useMemo(() => {
    const unitMap = new Map<string, NodeState | LineRecord>();
    [...units.nodes, ...units.lines].forEach(item => {
      unitMap.set(item.id, item)
    })

    return unitMap
  }, [units])

  const getUnitTag = (unitId: string) => {
    return unitMap.get(unitId)?.tag || '-'
  }

  const handleAdd = () => {
    const newRow = {
      id: uuidv4(),
      tag: tagGenerator.generateTag(EquipmentType.Set),
      isNew: true
    } as SetOperation
    const editRow = {...newRow, index: operations.length} as any
    dispatch(addSetOperation(newRow))
    setEditRow(editRow)
  };

  const getAttrsOptions = (unitId: string | undefined) => {
    if (!unitId) return []
    const unit = unitMap.get(unitId);
    if (!unit) {
      return []
    }
    return getUnitAttributes(unit)
  }

  const save = () => {
    if (!editRow) {
      return;
    }
    const {id, sourceUnit, sourceVar, targetUnit, targetVar, tag} = editRow
    const data: SetOperation = {
      id, sourceUnit, sourceVar, targetUnit, targetVar, tag
    }
    dispatch(updateSetOperation(data))
    setEditRow(undefined)
    onChange?.()
  }

  const  cancel = () => {
    if (!editRow) {
      return;
    }
    if (editRow.isNew) {
      dispatch(removeSetOperation(editRow.id))
    }
    setEditRow(undefined)
  }

  const deleteRow = (unitId: string) => {
    dispatch(removeSetOperation(unitId))
    onChange?.()
  }

  const toEdit = (item: SetOperation, index) => {
    setEditRow({...item, index})
  }

  const setChange = (attr: string, val: any) => {
    setEditRow({...editRow, [attr]: val} as any)
  }

  const getUnitVarAttribute = (attr: string | undefined, unitId: string | undefined) => {
    if (!attr || !unitId) return null
    const cell = unitMap.get(unitId)
    if (!cell) return null
    return getVarAttribute(attr, cell)
  }

  const getName = (attr: string, unitId: string) => {
    const attribute = getUnitVarAttribute(attr, unitId)
    if (!attribute) return attr;
    let attrName = attribute.label
    if (attribute.dimCode) {
      const unitEntity = getUnitEntity(attribute.dimCode)
      return `${attrName}[${unitEntity?.unit.code || '-'}]`
    }

    return attrName
  }

  return (
    <div>
      <table className="bordered-table table-mt">
        <thead>
          <tr>
            <th rowSpan={2}>Name</th>
            <th colSpan={2} style={{width: 320}}>Input</th>
            <th colSpan={2} style={{width: 320}}>Output</th>
            <th rowSpan={2} style={{width: 110}}>Factor</th>
            <th rowSpan={2} style={{width: 110}}>Offset</th>
            <th rowSpan={2}>Actions</th>
          </tr>
          <tr>
            <th scope="col" style={{width: 200}}>Equipment</th>
            <th style={{width: 120}}>Variable</th>
            <th scope="col" style={{width: 200}}>Equipment</th>
            <th style={{width: 120}}>Variable</th>
          </tr>
        </thead>
        <tbody>
          {operations.map((item, index) => {
            const unitSimData = data.find(sim => item.id === sim.uuid)
            const unitVars = unitSimData?.vars?.UNIT || {}
            const aliasVars = unitSimData?.vars?.ALIAS || {}

            const isEdit = index === editRow?.index

            const sourceVarName = getName(item.sourceVar, item.sourceUnit)
            const targetVarName = getName(item.targetVar, item.targetUnit)

            return <tr key={item.id}>
              <td>
                {isEdit? <Input value={editRow.tag} onChange={(e) => setChange('tag', e.target.value)}/> :
                  <Space>{item.tag}<UnitMessageDialog unitSim={unitSimData}/></Space>}
              </td>
              <td>
                {isEdit ? <UnitSelect
                  style={{width: '100%'}}
                  units={units}
                  value={editRow.sourceUnit}
                  onChange={(val) => {
                    setChange('sourceUnit', val)
                  }}
                  /> :
                  getUnitTag(item.sourceUnit) }
              </td>
              <td>
                {
                  isEdit ? <Select
                  defaultValue={editRow?.sourceVar}
                  options={getAttrsOptions(editRow?.sourceUnit)}
                  onChange={(val) => setChange('sourceVar', val)}
                  style={{ width: '100%' }} /> : <div style={{width: '100%'}} className='flex-space-btw'>
                    <div className={styles.varName}>{sourceVarName}</div>
                    <ManualInput
                      style={{width: 100}}
                      unit={item}
                      dimCode={getUnitVarAttribute(editRow?.sourceVar, editRow?.sourceUnit)?.dimCode}
                      attrKey=':ALIAS:INPUT'
                      data={aliasVars.INPUT}/>
                  </div>
                }
                
              </td>
              <td>
                {isEdit ? <UnitSelect
                  style={{width: '100%'}}
                  units={units}
                  value={editRow.targetUnit}
                  onChange={(val) => {
                    setChange('targetUnit', val)
                  }}
                /> :
                  getUnitTag(item.targetUnit) }
              </td>
              <td>
                {
                  isEdit ? <Select
                  defaultValue={editRow?.targetVar}
                  options={getAttrsOptions(editRow?.targetUnit)}
                  onChange={(val) => setChange('targetVar', val)}
                  style={{ width: '100%' }} /> : <div style={{width: '100%'}} className='flex-space-btw'>
                    <div className={styles.varName}>{targetVarName}</div>
                    <ManualInput
                      style={{width: 100}}
                      unit={item}
                      dimCode={getUnitVarAttribute(editRow?.targetVar, editRow?.targetUnit)?.dimCode}
                      attrKey=':ALIAS:OUTPUT'
                      data={aliasVars.OUTPUT}/>
                  </div>
                }
              </td>
              <td><ManualInput
                style={{width: '100%'}}
                unit={item}
                attrKey=':UNIT:FACTOR'
                data={unitVars.FACTOR}
                /></td>
              <td><ManualInput
                style={{width: '100%'}}
                unit={item}
                attrKey=':UNIT:OFFSET'
                data={unitVars.OFFSET}/></td>
              <td>
                {
                  isEdit && <>
                    <Space>
                      <Button size='small' onClick={save} icon={<CheckOutlined />} type='primary' />
                      <Button size='small' onClick={cancel} icon={<CloseOutlined />} danger />
                    </Space>
                  </>
                }
                {!isEdit && <Space>
                  <Button size='small' icon={<FormOutlined />} type='default' onClick={() => toEdit(item, index)} />
                  <Button size='small' icon={<DeleteOutlined />} danger onClick={() => deleteRow(item.id)} />
                </Space>}
              </td>
            </tr>
          })}
        </tbody>
      </table>
      <Button type="primary" onClick={handleAdd} style={{ marginTop: 16, width: 200, marginBottom: 40 }}>
        + Add
      </Button>
    </div>
  );
};
